import { useTurnstile } from "../TurnstileContext";
import { LastUsedBadge } from "../base-ui/LastUsedBadge";
import { trackAuthFormSubmit } from "../trackAuthFormSubmit";
import { AUTH_VIEWS, ViewType } from "../types";
import { getDeeplinkSearchParamsString } from "../utils";
import { Button, Input, Message } from "./../base-ui/index";
import { requestSignupEmailMutationOptions } from "@/hooks/mutations/auth/requestSignupEmailMutationOptions";
import { signinMutationOptions } from "@/hooks/mutations/auth/signinMutationOptions";
import {
  ssoRedirectCheckMutationOptions,
  ssoSignInMutationOptions,
} from "@/hooks/mutations/auth/ssoSignInMutationOptions";
import { LocalStorageKey, useLocalStorage } from "@/hooks/useLocalStorage";
import { assertNever } from "@/packages/ts-utils";
import { buildMarketingURL } from "@/utils/helpers";
import { getSsoProbeClient } from "@/utils/supabase/client";
import type { Database } from "@/utils/types";
import {
  I18nVariables,
  VIEWS,
  ViewSignIn,
  ViewSignUp,
} from "@supabase/auth-ui-shared";
import { SupabaseClient } from "@supabase/supabase-js";
import { useMutation } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";

export interface EmailAuthProps {
  authView?: ViewSignIn | ViewSignUp;
  setAuthView: (view: ViewType) => void;
  supabaseClient: SupabaseClient<Database>;
  showLinks?: boolean;
  i18n?: I18nVariables;
  children?: React.ReactNode;
}

function EmailAuth({
  authView = "sign_in",
  setAuthView,
  supabaseClient,
  showLinks = false,
  i18n,
  children,
}: EmailAuthProps) {
  const [email, setEmail] = useState("");
  const emailRef = useRef(email);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { getToken, passwordLoginMode } = useTurnstile();
  const ssoProbeClient = getSsoProbeClient();

  const router = useRouter();
  const searchParams = useSearchParams();
  const [_savedEmail, setSavedEmail] = useLocalStorage(
    LocalStorageKey.UnverifiedEmail,
    ""
  );
  const [lastAuthMethod] = useLocalStorage(
    LocalStorageKey.LastAuthMethod,
    null
  );

  const isSignUp = authView === "sign_up";
  // Sign-in reveals the password field only after the email step, matching the
  // "Continue with Email" flow in the design.
  const [passwordStepEmail, setPasswordStepEmail] = useState<string | null>(
    null
  );
  const isPasswordStepShown = passwordStepEmail === email;
  const showPasswordField = !isSignUp && isPasswordStepShown;

  const onMutationError = (err: Error) => {
    setError(err.message || "An error occurred. Please try again.");
  };

  const ssoSignInMutation = useMutation({
    ...ssoSignInMutationOptions({
      supabaseClient,
      getTurnstileToken: getToken,
      deeplink: searchParams.get("deeplink"),
    }),
    onSuccess: (ssoRedirectUrl) => {
      window.location.href = ssoRedirectUrl;
    },
    onError: onMutationError,
  });

  // The email step determines whether password auth is allowed for the domain.
  const ssoRedirectCheckMutation = useMutation({
    ...ssoRedirectCheckMutationOptions({
      ssoProbeClient,
      passwordLoginMode,
      getTurnstileToken: getToken,
    }),
    onSuccess: (probe, { email: probedEmail }) => {
      if (probedEmail !== emailRef.current) {
        return;
      }
      if (probe.kind === "sso") {
        ssoSignInMutation.mutate({ email: probedEmail });
        return;
      }
      setPasswordStepEmail(probedEmail);
    },
    // The probe is best-effort: on failure fall through to password auth,
    // where signinMutation re-checks SSO before accepting the password.
    onError: (_error, { email: probedEmail }) =>
      setPasswordStepEmail(probedEmail),
  });
  const ssoProbe =
    ssoRedirectCheckMutation.variables?.email === email
      ? ssoRedirectCheckMutation.data
      : undefined;

  const requestSignupEmailMutation = useMutation({
    ...requestSignupEmailMutationOptions(getToken),
    onSuccess: (response, { email: checkedEmail }) => {
      if (checkedEmail !== emailRef.current) return;
      switch (response.action) {
        case "sso":
          ssoSignInMutation.mutate({ email: checkedEmail });
          return;
        case "book_demo":
          window.location.href = buildMarketingURL({ path: "/book-my-demo" });
          return;
        case "email_sent": {
          setSavedEmail(checkedEmail);
          router.push(
            `/verify-email${getDeeplinkSearchParamsString(searchParams)}`
          );
          return;
        }
        default:
          assertNever(response);
      }
    },
    onError: onMutationError,
  });

  const signinMutation = useMutation({
    ...signinMutationOptions({
      supabaseClient,
      ssoProbeClient,
      passwordLoginMode,
      getTurnstileToken: getToken,
      deeplink: searchParams.get("deeplink"),
    }),
    onSuccess: (result) => {
      switch (result.kind) {
        case "ssoRedirect":
          window.location.href = result.url;
          return;
        case "mfaChallenge":
          setAuthView(AUTH_VIEWS.MFA_CHALLENGE);
          return;
        case "signedIn":
          // A full page load: a client refresh can replay a redirect Next kept from before sign-in, and loop.
          window.location.reload();
          return;
        default:
          return assertNever(result);
      }
    },
    onError: onMutationError,
  });

  // load email from url parameter
  useEffect(() => {
    const emailFromUrl = searchParams.get("email");
    if (emailFromUrl && email === "") {
      emailRef.current = emailFromUrl;
      setEmail(emailFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // only first run / when search params change

  const labels = i18n?.[authView];

  const loading =
    requestSignupEmailMutation.isPending ||
    requestSignupEmailMutation.data?.action === "email_sent" ||
    signinMutation.isPending ||
    signinMutation.isSuccess ||
    ssoSignInMutation.isPending ||
    ssoSignInMutation.isSuccess ||
    ssoRedirectCheckMutation.isPending;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    trackAuthFormSubmit({
      input: email,
      action: isSignUp
        ? "sign_up"
        : isPasswordStepShown
          ? "sign_in"
          : "check_email",
    });
    if (!email || email.trim() === "") {
      setError("Please enter your email address");
      return;
    }
    if (isSignUp) {
      requestSignupEmailMutation.mutate({ email });
      return;
    }
    if (!isPasswordStepShown) {
      ssoRedirectCheckMutation.mutate({ email });
      return;
    }

    // The email step already completed the retrying SSO check for this address,
    // so a second check would only burn rate limit and a Turnstile token.
    const wasEmailProbedWithoutSso =
      ssoRedirectCheckMutation.variables?.email === email &&
      ssoProbe?.kind === "none";
    signinMutation.mutate({
      email,
      password,
      confirmedNoSso: wasEmailProbedWithoutSso
        ? { email, kind: "none" }
        : undefined,
    });
  };

  const getButtonLabel = () => {
    if (ssoRedirectCheckMutation.isPending) {
      return "Checking...";
    }
    if (loading) {
      return labels?.loading_button_label;
    }
    if (isSignUp) {
      return "Continue";
    }
    return isPasswordStepShown ? "Sign in" : "Continue with Email";
  };

  return (
    <form
      id={authView === "sign_in" ? `auth-sign-in` : `auth-sign-up`}
      onSubmit={handleFormSubmit}
      autoComplete={"on"}
      className="w-full"
    >
      <div className="flex flex-col gap-3 text-left">
        <Input
          id="email"
          type="email"
          name="email"
          disabled={
            requestSignupEmailMutation.isPending ||
            requestSignupEmailMutation.data?.action === "email_sent"
          }
          placeholder="Email Address"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            emailRef.current = e.target.value;
            setEmail(e.target.value);
            // A new address invalidates the previous domain's SSO probe.
            ssoRedirectCheckMutation.reset();
            setPasswordStepEmail(null);
          }}
          autoComplete="username"
          value={email}
        />
        {showPasswordField && (
          <div>
            <Input
              id="password"
              type="password"
              name="password"
              autoFocus
              placeholder={labels?.password_input_placeholder}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              autoComplete="current-password"
              value={password}
            />
          </div>
        )}
        {children}
        <Button type="submit" color="primary" loading={loading}>
          {getButtonLabel()}
          {authView === VIEWS.SIGN_IN &&
            !loading &&
            lastAuthMethod === "email" && <LastUsedBadge />}
        </Button>
        {showLinks && authView === VIEWS.SIGN_IN && (
          <p className="text-center text-sm text-content-tertiary">
            Forgot password?{" "}
            <a
              href="auth?view=forgotten_password"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                setAuthView(VIEWS.FORGOTTEN_PASSWORD);
              }}
              className="text-blue-500 hover:text-blue-600"
            >
              Reset password
            </a>
          </p>
        )}
        {error && <Message color="danger">{error}</Message>}
      </div>
    </form>
  );
}

export { EmailAuth };
